'use client'

import * as React from 'react'

import { Check, ChevronsUpDown, Loader2 } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'

import { useDebounce } from '@/hooks/use-debouncer'
import { useDynamicQuery } from '@/hooks/use-dynamic-query'
import { useIntersectionObserver } from '@/hooks/use-intersection-observer'

import { cn } from '@/utils'

import type { ComboboxProps } from './combobox-types'

import { filterItems, getItemLabel } from './utils'

export function Combobox<T extends object>({
  classNames,
  closeOnSelect = true,
  customTrigger,
  disabled = false,
  getOptionLabel,
  infinite = false,
  initialItem,
  labelKey,
  labelKeys,
  onChange,
  onSearch,
  onSelectItem,
  options,
  placeholder,
  popoverContentProps,
  queryOptions,
  renderOption,
  renderSelected,
  searchDebounceMs = 300,
  value,
  valueKey
}: ComboboxProps<T>) {
  const t = useTranslations('components.form.autocomplete')
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState('')
  const [searchQuery, setSearchQuery] = React.useState('')

  const itemsCacheRef = React.useRef<Map<string, T>>(
    (() => {
      const map = new Map<string, T>()
      if (initialItem) {
        const key = String(initialItem[valueKey] ?? '')
        if (key) map.set(key, initialItem as T)
      }
      return map
    })()
  )

  React.useEffect(() => {
    if (initialItem) {
      const key = String(initialItem[valueKey] ?? '')
      if (key) itemsCacheRef.current.set(key, initialItem as T)
    }
  }, [initialItem, valueKey])

  const debouncedSearch = useDebounce((query: string) => {
    setSearchQuery(query)
    onSearch?.(query)
  }, searchDebounceMs)

  const handleSearch = React.useCallback(
    (query: string) => {
      setInputValue(query)
      debouncedSearch(query)
    },
    [debouncedSearch]
  )

  const handleSelect = React.useCallback(
    (selectedValue: string, selectedItem?: T) => {
      const newValue = selectedValue === value ? undefined : selectedValue
      onChange?.(newValue)
      onSelectItem?.(selectedItem)
      if (closeOnSelect) {
        setOpen(false)
      }
    },
    [closeOnSelect, onChange, onSelectItem, value]
  )

  const lastItemRef = React.useRef<HTMLDivElement>(null)
  const isStaticMode = !!options && !queryOptions

  const cacheItem = React.useCallback(
    (item: T) => {
      const itemValue = String(item[valueKey] ?? '')
      if (itemValue) {
        itemsCacheRef.current.set(itemValue, item)
      }
    },
    [valueKey]
  )

  // Store cached item separately to avoid ref access during render
  const [cachedSelectedItem, setCachedSelectedItem] = React.useState<
    T | undefined
  >()

  /**
   * If the selected value hasn't been resolved to a cached item yet, ask
   * the query hook to fetch just that id (instead of the normal search) so
   * the label can be resolved without requiring the user to open/search.
   * Once resolved, `cachedSelectedItem` becomes set and this clears.
   */
  const missingId = value && !cachedSelectedItem ? value : undefined

  const {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    items: queryItems
  } = useDynamicQuery<T>({
    ids: missingId ? [missingId] : [],
    infinite,
    isStaticMode,
    queryOptions,
    searchQuery
  })

  const items = React.useMemo(() => {
    if (isStaticMode && options) {
      return filterItems(options, inputValue, labelKey, labelKeys)
    }

    const seen = new Set<string>()
    return queryItems.filter((item) => {
      const key = String(item[valueKey] ?? '')
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }, [
    inputValue,
    isStaticMode,
    labelKey,
    labelKeys,
    options,
    queryItems,
    valueKey
  ])

  React.useEffect(() => {
    items.forEach(cacheItem)
  }, [cacheItem, items])

  useIntersectionObserver(lastItemRef, fetchNextPage, {
    enabled: infinite && open && items.length > 0,
    hasNextPage,
    isFetchingNextPage,
    itemsCount: items.length
  })

  const selectedItem = React.useMemo(() => {
    if (!value) return undefined

    return items.find((item) => String(item[valueKey]) === String(value))
  }, [items, value, valueKey])

  /**
   * Update cached item when value changes (or once items/cache are seeded).
   *
   * Important: once `cachedSelectedItem` already matches the current
   * `value`, leave it alone — do NOT clear it just because `selectedItem`
   * happens to be found this render. Clearing it would flip `missingId`
   * back on, which switches the query to `{ ids }` mode, which changes
   * `items` (new query key), which makes `selectedItem` toggle again,
   * re-triggering this effect — an infinite oscillation between the two
   * query modes ("Maximum update depth exceeded"). Only resolve/update
   * when the cache doesn't yet correspond to this value.
   */
  React.useEffect(() => {
    if (!value) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs state from the mutable items cache ref, which cannot be read during render
      setCachedSelectedItem(undefined)
      return
    }

    setCachedSelectedItem((prev) => {
      if (prev && String(prev[valueKey]) === String(value)) return prev
      if (selectedItem) return selectedItem
      return itemsCacheRef.current.get(String(value))
    })
  }, [initialItem, selectedItem, value, valueKey])

  const finalSelectedItem = selectedItem || cachedSelectedItem
  const selectedLabel = finalSelectedItem
    ? getItemLabel(finalSelectedItem, labelKey, labelKeys)
    : ''
  const hasCustomSelectedRender = Boolean(renderSelected && finalSelectedItem)

  const displayValue = React.useMemo(() => {
    if (renderSelected && finalSelectedItem) {
      return renderSelected(finalSelectedItem)
    }

    return selectedLabel || placeholder || t('placeholder')
  }, [finalSelectedItem, placeholder, renderSelected, selectedLabel, t])

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        {customTrigger ? (
          customTrigger
        ) : (
          <Button
            aria-expanded={open}
            className={cn(
              'h-[42px] w-full max-w-full min-w-0 justify-between rounded-md! text-sm!',
              selectedLabel ? 'text-foreground' : 'text-muted-foreground',
              classNames?.trigger
            )}
            disabled={disabled || isLoading}
            role="combobox"
            title={selectedLabel || undefined}
            variant="outline"
            wrapChildren={false}
          >
            <div className="flex min-w-0 flex-1 text-start">
              {hasCustomSelectedRender ? (
                <div className="min-w-0 flex-1 overflow-hidden text-sm">
                  {displayValue}
                </div>
              ) : (
                <span className="min-w-0 flex-1 truncate text-sm font-normal">
                  {displayValue}
                </span>
              )}
            </div>
            {isLoading ? (
              <Loader2 className="ms-2 h-4 w-4 shrink-0 animate-spin opacity-50" />
            ) : (
              <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
            )}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn(
          'w-(--radix-popover-trigger-width) min-w-56 p-0',
          classNames?.popoverContent
        )}
        {...popoverContentProps}
      >
        <Command
          shouldFilter={
            !infinite && !isStaticMode && typeof queryOptions !== 'function'
          }
        >
          <CommandInput
            onValueChange={
              infinite || isStaticMode || typeof queryOptions === 'function'
                ? handleSearch
                : undefined
            }
            placeholder={t('searchPlaceholder')}
            value={
              infinite || isStaticMode || typeof queryOptions === 'function'
                ? inputValue
                : undefined
            }
          />
          <CommandList>
            {isLoading && items.length === 0 ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-muted-foreground ms-2 text-sm">
                  {t('loading')}
                </span>
              </div>
            ) : items.length === 0 ? (
              <CommandEmpty>{t('noItemsFound')}</CommandEmpty>
            ) : (
              <CommandGroup>
                {items.map((item, index) => {
                  const itemValue = String(item[valueKey] ?? '')
                  const itemLabel = getItemLabel(item, labelKey, labelKeys)
                  const isSelected = value === itemValue
                  const isLastItem = infinite && index === items.length - 1

                  return (
                    <div
                      key={itemValue || index}
                      ref={(el) => {
                        if (isLastItem) {
                          lastItemRef.current = el
                        }
                      }}
                    >
                      <CommandItem
                        className="cursor-pointer"
                        onSelect={() => handleSelect(itemValue, item)}
                        value={itemLabel}
                      >
                        {renderOption ? (
                          <span className="pointer-events-none">
                            {renderOption(item)}
                          </span>
                        ) : getOptionLabel ? (
                          <span className="pointer-events-none">
                            {getOptionLabel(item)}
                          </span>
                        ) : (
                          <span className="min-w-0 break-words">{itemLabel}</span>
                        )}
                        {isSelected && <Check className="ms-auto h-4 w-4" />}
                      </CommandItem>
                    </div>
                  )
                })}
                {infinite && isFetchingNextPage && (
                  <div className="flex items-center justify-center py-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-muted-foreground ms-2 text-sm">
                      {t('loading')}
                    </span>
                  </div>
                )}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
